/**
 * Storage utilities for persisting Advanced Retirement Calculator settings
 */

import {
  buildStorageKey,
  createVersionedPayload,
  parseVersionedPayload,
  migrateLifeEvent,
  migrateToScenarios,
  type AssetCategory,
  type WithdrawalMode,
  type LifeEvent,
  type LifeEventScenario,
  type PartnerInputs,
  type ReturnRiskAssumption,
  type RiskProfile,
  type RetirementGoal,
  type ProjectionResult,
  type PensionBreakdown,
  mapLegacySigmaToProfile,
  deriveSigmaFromProfile,
  findProfileById,
  migrateProfileId,
  DEFAULT_PROFILE_BY_CATEGORY,
} from "~/domain/retirement/advancedRetirement";
import type { PensionProductPlan } from "~/domain/retirement/pensionLaw";
import { LEGACY_STORAGE_KEY } from "./uiConstants";

/** Breakdown totals shape for persisted calculated data (matches BreakdownTab) */
export interface PersistedBreakdownTotals {
  liquidTotal: number;
  nonLiquidTotal: number;
  totalAssets: number;
  totalDebts: number;
  netWorth: number;
  spendableBeforeRetirement: number;
}

/**
 * Calculated data from a projection run — persisted so UI can show last run without recalculating.
 */
export interface PersistedCalculatedData {
  result: ProjectionResult;
  pensionResult: {
    perProductPlan: PensionProductPlan[];
    pensionBreakdown: PensionBreakdown;
  } | null;
  breakdownTotals: PersistedBreakdownTotals;
}

/**
 * Build the localStorage key for calculated data (same base as params + ":data").
 */
export function buildCalculatedDataKey(
  userId: string | number,
  affiliateId?: string | number | null
): string {
  return `${buildStorageKey(userId, affiliateId)}:data`;
}

/**
 * Per-product custom yield settings
 * Maps productId to custom return assumptions
 */
export type ProductCustomYields = Record<string, Partial<ReturnRiskAssumption>>;

/**
 * User settings that should be persisted (not auto-derived from products)
 */
export interface PersistedSettings {
  currentAge: number;
  retirementAge: number;
  /** Pension eligibility / start age chosen by the user in the pension simulator */
  pensionEligibilityAge?: number;
  lifeExpectancy: number;
  monthlySpend?: number;
  monthlyIncome?: number;
  monthlySavings?: number;
  /** Marital status — affects pension mekadem */
  maritalStatus?: string;
  /**
   * Second partner in a couple household (couple mode). When absent, the
   * calculator runs in single-person mode.
   */
  partner?: PartnerInputs;
  /**
   * @deprecated Product-to-partner attribution is now derived automatically from
   * the owning Moneytor account (`user_id`) and can no longer be reassigned by
   * users. Kept only so old persisted payloads still parse; the value is ignored.
   */
  productOwnerOverrides?: Record<string, 0 | 1>;
  withdrawalRate: number;
  inflation: number;
  withdrawalMode: WithdrawalMode;
  includeNonLiquidRealestate: boolean;
  includeNonLiquidPension: boolean;
  includeUnvestedRSU: boolean;
  returnAssumptions: Record<AssetCategory, ReturnRiskAssumption>;
  lifeEvents: LifeEvent[];
  /** Life event scenarios (new format — lifeEvents is kept for migration) */
  scenarios?: LifeEventScenario[];
  runMonteCarlo: boolean;
  monteCarloSimulations: number;
  gender?: "male" | "female";
  /** Per-product custom yield settings (overrides category defaults) */
  productCustomYields?: ProductCustomYields;
  /** User-created risk profiles */
  customRiskProfiles?: RiskProfile[];
  /** Whether the risk-profile migration banner has been dismissed */
  riskProfileMigrationDismissed?: boolean;
  /** User-defined retirement goals */
  goals?: RetirementGoal[];
  /** Product IDs that are excluded (unchecked) in the breakdown — persisted so toggles survive reload */
  excludedProductIds?: (string | number)[];
  /**
   * @deprecated Replaced by debtIncludedInExpenses. Kept for migration only.
   * Old flag indicating that monthlySpend already excluded active debt repayments.
   */
  debtExcludedFromSpend?: boolean;
  /**
   * Flag indicating that monthlySpend INCLUDES active debt repayments (the
   * "includedInExpenses" model). Auto-generated loan events are marked with
   * `includedInExpenses: true` so the projection engine does NOT double-count them.
   * When absent, the calculator handles backward-compatible migration.
   */
  debtIncludedInExpenses?: boolean;
}

/**
 * Migrate legacy stdDev-based return assumptions to risk-profile-based.
 *
 * For each category: if riskProfileId is missing but standardDeviation exists,
 * find the closest built-in profile by implied sigma and assign it.
 *
 * Returns true if any migration was performed.
 */
export function migrateReturnAssumptionsToProfiles(
  assumptions: Record<string, ReturnRiskAssumption>
): boolean {
  let migrated = false;

  for (const [category, assumption] of Object.entries(assumptions)) {
    if (
      !assumption.riskProfileId &&
      assumption.standardDeviation !== undefined
    ) {
      const legacySigmaPct = assumption.standardDeviation * 100;
      const profileId =
        legacySigmaPct > 0
          ? mapLegacySigmaToProfile(legacySigmaPct)
          : DEFAULT_PROFILE_BY_CATEGORY[category as AssetCategory] ?? "solid";

      assumption.riskProfileId = profileId;
      assumption.legacyStdDevPct = legacySigmaPct;

      // Update standardDeviation to match the profile-derived value
      const profile = findProfileById(profileId);
      if (profile) {
        assumption.standardDeviation = deriveSigmaFromProfile(profile);
      }

      migrated = true;
    }
  }

  return migrated;
}

/**
 * Load persisted settings from versioned localStorage
 */
export function loadPersistedSettings(
  userId: string | number,
  affiliateId?: string | number | null
): {
  settings: Partial<PersistedSettings> | null;
  timestamp: number | null;
  needsMigration: boolean;
  needsRiskProfileMigration: boolean;
} {
  try {
    // Only load data saved with the current schema version (v3).
    // Older versions (v1, v2, legacy) are intentionally discarded so that
    // outdated risk-profile deltas and default returns don't interfere.
    const storageKey = buildStorageKey(userId, affiliateId);
    let stored = window.localStorage.getItem(storageKey);
    let payload = stored ? parseVersionedPayload<PersistedSettings>(stored) : null;

    // Backward-compatible load: accept the previous v6 payload (before couple
    // support) so existing users keep their settings. All new partner fields are
    // optional and couple mode defaults off when they're absent.
    if (!payload) {
      const v6Key = storageKey.replace(":v7:", ":v6:");
      if (v6Key !== storageKey) {
        const v6Stored = window.localStorage.getItem(v6Key);
        if (v6Stored) {
          try {
            const parsed = JSON.parse(v6Stored);
            if (
              parsed &&
              typeof parsed === "object" &&
              parsed.version === 6 &&
              "data" in parsed
            ) {
              payload = parsed as typeof payload;
              stored = v6Stored;
            }
          } catch {
            /* ignore malformed v6 payload */
          }
        }
      }
    }

    if (stored) {
      if (payload) {
        const settings = payload.data;

        // Migrate any legacy life events
        if (settings.lifeEvents) {
          settings.lifeEvents = settings.lifeEvents.map(migrateLifeEvent);
        }
        // Migrate flat lifeEvents to scenarios if needed
        if (!settings.scenarios && settings.lifeEvents) {
          settings.scenarios = migrateToScenarios(settings.lifeEvents);
        } else if (settings.scenarios) {
          settings.scenarios = settings.scenarios.map((s) => ({
            ...s,
            events: s.events.map(migrateLifeEvent),
          }));
        }

        // Migrate old profile IDs (e.g. "balanced" → "solid") to the current 5-profile set
        if (settings.returnAssumptions) {
          for (const assumption of Object.values(settings.returnAssumptions)) {
            if (assumption.riskProfileId) {
              const migrated = migrateProfileId(assumption.riskProfileId);
              if (migrated !== assumption.riskProfileId) {
                assumption.riskProfileId = migrated;
                const profile = findProfileById(migrated);
                if (profile) {
                  assumption.standardDeviation =
                    deriveSigmaFromProfile(profile);
                }
              }
            }
          }
        }

        // Risk profile migration: older data may have stdDev but no riskProfileId
        let needsRiskProfileMigration = false;
        if (settings.returnAssumptions) {
          needsRiskProfileMigration = migrateReturnAssumptionsToProfiles(
            settings.returnAssumptions as Record<string, ReturnRiskAssumption>
          );
        }

        return {
          settings,
          timestamp: payload.timestamp,
          needsMigration: false,
          needsRiskProfileMigration:
            needsRiskProfileMigration &&
            !settings.riskProfileMigrationDismissed,
        };
      }
    }
  } catch (e) {
    console.error("Failed to load retirement settings:", e);
  }
  return {
    settings: null,
    timestamp: null,
    needsMigration: false,
    needsRiskProfileMigration: false,
  };
}

/**
 * Load persisted calculated data from localStorage.
 * Returns null if missing, invalid, or wrong schema version.
 */
export function loadCalculatedData(
  userId: string | number,
  affiliateId?: string | number | null
): PersistedCalculatedData | null {
  try {
    const storageKey = buildCalculatedDataKey(userId, affiliateId);
    const stored = window.localStorage.getItem(storageKey);
    if (!stored) return null;

    const payload = parseVersionedPayload<PersistedCalculatedData>(stored);
    if (!payload?.data) return null;

    const { result, breakdownTotals } = payload.data;
    if (
      !result ||
      !Array.isArray(result.deterministicProjection) ||
      result.deterministicProjection.length === 0 ||
      !breakdownTotals ||
      typeof breakdownTotals.liquidTotal !== "number"
    ) {
      return null;
    }

    return payload.data;
  } catch (e) {
    console.error("Failed to load retirement calculated data:", e);
    return null;
  }
}

/**
 * Save calculated data to versioned localStorage (called after each successful run).
 */
export function saveCalculatedData(
  data: PersistedCalculatedData,
  userId: string | number,
  affiliateId?: string | number | null
): void {
  try {
    const storageKey = buildCalculatedDataKey(userId, affiliateId);
    const payload = createVersionedPayload(data);
    window.localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch (e) {
    console.error("Failed to save retirement calculated data:", e);
  }
}

/**
 * Save settings to versioned localStorage
 */
export function savePersistedSettings(
  settings: PersistedSettings,
  userId: string | number,
  affiliateId?: string | number | null
): void {
  try {
    const storageKey = buildStorageKey(userId, affiliateId);
    const payload = createVersionedPayload(settings);
    window.localStorage.setItem(storageKey, JSON.stringify(payload));

    // Clean up old versioned and legacy keys (now that v7 has been written)
    for (const v of ["v6", "v5", "v4", "v3", "v2", "v1"]) {
      const oldKey = storageKey.replace(":v7:", `:${v}:`);
      if (oldKey !== storageKey) window.localStorage.removeItem(oldKey);
    }
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch (e) {
    console.error("Failed to save retirement settings:", e);
  }
}

/**
 * Clear persisted settings — removes all known storage keys
 * (current v7, old v6..v1, legacy unversioned key, and calculated data key).
 */
export function clearPersistedSettings(
  userId: string | number,
  affiliateId?: string | number | null
): void {
  try {
    const storageKey = buildStorageKey(userId, affiliateId);
    window.localStorage.removeItem(storageKey);
    window.localStorage.removeItem(buildCalculatedDataKey(userId, affiliateId));
    // Also remove old versioned keys
    for (const v of ["v6", "v5", "v4", "v3", "v2", "v1"]) {
      const oldKey = storageKey.replace(":v7:", `:${v}:`);
      if (oldKey !== storageKey) window.localStorage.removeItem(oldKey);
    }
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch (e) {
    console.error("Failed to clear retirement settings:", e);
  }
}
